/**
 * Scan hours for one device, in the Edge Fleet drawer (release 3.0.1).
 *
 * Laptops slowed down while scans ran. Besides running the scan in the
 * background under a CPU ceiling, an operator can now keep scheduled scans to
 * set hours of the day - lunch time, or after hours on machines that stay on.
 * The hours travel on the existing config override (`scanWindow`), in the
 * device's own local time; the agent keeps them until they are changed, and a
 * scan still running when they end stops and continues in the next window.
 *
 * Two readings are shown on purpose, as everywhere in this drawer: what the
 * console ASKED for (the override) and what the agent says it is RUNNING (its
 * effective config). They differ until the agent's next check-in, and on an
 * agent older than 0.0.13, which does not know scan hours at all.
 */
import { useState } from 'react';
import { Clock, Loader2 } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { pushEdgeConfigOverride, type EdgePodDetail } from '@/lib/api';

import { sourceText } from './scan-policy-format';

/** The controller's rule (PushConfigOverrideSchema.scanWindow), repeated for a friendly error. */
export const SCAN_WINDOW_PATTERN = /^([01]\d|2[0-3]):[0-5]\d-([01]\d|2[0-3]):[0-5]\d$/;

/** The window text from the two time inputs, or what is wrong with them. */
export function scanWindowFromInputs(start: string, end: string): { window: string } | { error: string } {
  if (!start || !end) return { error: 'Give both a start and an end time.' };
  const window = `${start}-${end}`;
  if (!SCAN_WINDOW_PATTERN.test(window)) return { error: 'Use 24-hour times, for example 12:30 and 14:00.' };
  return { window };
}

/** A window in plain words. */
export function describeScanWindow(window: string | null | undefined): string {
  if (!window) return 'Any time of day';
  const [start, end] = window.split('-');
  if (!start || !end) return window;
  if (start === end) return 'Any time of day';
  return start < end
    ? `Between ${start} and ${end}, the device's local time`
    : `From ${start} until ${end} the next morning, the device's local time`;
}

interface Props {
  pod: EdgePodDetail;
  onSaved: () => void | Promise<void>;
}

export function EdgePodScanHours({ pod, onSaved }: Props): JSX.Element {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const patch = pod.activeOverride?.patch;
  const asked =
    patch && Object.hasOwn(patch, 'scanWindow') ? ((patch['scanWindow'] as string | null) ?? null) : undefined;
  const running = pod.effectiveConfig;
  // Agents from 0.0.13 report their scan priority; older ones cannot hold hours.
  const agentKnowsHours = !!running && Object.hasOwn(running, 'scanPriority');
  const runningWindow = running && typeof running['scanWindow'] === 'string' ? (running['scanWindow'] as string) : null;
  const runningSource =
    running && typeof running['scanWindowSource'] === 'string' ? (running['scanWindowSource'] as string) : null;

  async function push(value: string | null): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await pushEdgeConfigOverride(pod.podId, { scanWindow: value });
      setStart('');
      setEnd('');
      await onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not save the scan hours');
    } finally {
      setBusy(false);
    }
  }

  function save(): void {
    const r = scanWindowFromInputs(start, end);
    if ('error' in r) {
      setError(r.error);
      return;
    }
    void push(r.window);
  }

  const disabled = busy || !!pod.decommissionedAt;
  // Release 3.3: the hours in force and the level they come from (device,
  // group, business unit, tenant, install). Absent on an older controller.
  const policy = pod.scanPolicy ?? null;
  const inherited =
    policy && policy.effective.source !== 'device'
      ? policy.effective.source === 'group' || policy.effective.source === 'business_unit' || policy.effective.source === 'tenant'
      : false;
  return (
    <GlassCard className="p-3">
      <CardHeader className="p-0 mb-2">
        <CardTitle className="text-sm inline-flex items-center gap-1.5">
          <Clock className="h-4 w-4" /> Scan hours
        </CardTitle>
        <CardDescription className="text-[11px]">
          Scheduled scans on this device run only inside these hours, in its own local time, and a
          scan still running when they end stops and carries on in the next window. A scan you ask
          for with Rescan is not held back. Delivered on the device&apos;s next check-in.
        </CardDescription>
      </CardHeader>
      <div className="space-y-2 text-xs">
        {policy ? (
          <div className="glass-lite rounded p-2" data-testid="scan-hours-in-force">
            <span className="text-muted-foreground">In force: </span>
            <span className="text-foreground">{describeScanWindow(policy.effective.window)}</span>
            <span className="text-muted-foreground">
              {' '}
              (from {sourceText(policy.effective, policy.levels.businessUnit?.name ?? null)})
              {policy.delivered === false ? ' - reaches the device on its next check-in' : ''}
            </span>
            {policy.groups.length > 0 ? (
              <div className="mt-1 text-[11px] text-muted-foreground" data-testid="scan-hours-groups">
                Groups:{' '}
                {policy.groups
                  .map((g) => `${g.name} (${g.via === 'token' ? 'enrolment token' : g.via === 'rule' ? 'rule' : 'added'}, priority ${g.priority})`)
                  .join(', ')}
              </div>
            ) : null}
          </div>
        ) : null}
        <div>
          <span className="text-muted-foreground">This device&apos;s own setting: </span>
          <span data-testid="scan-hours-asked">
            {asked === undefined || asked === null
              ? inherited
                ? `not set (${sourceText(policy!.effective, policy!.levels.businessUnit?.name ?? null)} applies)`
                : asked === null
                  ? 'cleared (the install setting applies)'
                  : 'not set (the install setting applies)'
              : describeScanWindow(asked)}
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Device reports: </span>
          <span data-testid="scan-hours-running">
            {!running
              ? 'nothing yet'
              : !agentKnowsHours
                ? 'this agent version does not support scan hours (0.0.13 or later does)'
                : `${describeScanWindow(runningWindow)}${runningSource ? ` (set by the ${runningSource})` : ''}`}
          </span>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-0.5">
            <span className="text-muted-foreground">From</span>
            <input
              type="time"
              aria-label="Scan hours from"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="glass-lite rounded px-2 py-1 bg-background/40 border border-border/40"
            />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-muted-foreground">Until</span>
            <input
              type="time"
              aria-label="Scan hours until"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="glass-lite rounded px-2 py-1 bg-background/40 border border-border/40"
            />
          </label>
          <Button size="sm" disabled={disabled} onClick={save}>
            {busy ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : null}
            Save hours
          </Button>
          <Button size="sm" variant="outline" disabled={disabled} onClick={() => void push('00:00-00:00')}>
            Any time
          </Button>
          <Button size="sm" variant="ghost" disabled={disabled || asked === undefined} onClick={() => void push(null)}>
            Clear this device&apos;s setting
          </Button>
        </div>
        {error && (
          <div role="alert" className="text-destructive">
            {error}
          </div>
        )}
      </div>
    </GlassCard>
  );
}
